import { ReadMoreHtml } from "@/components/read-more/ReadMoreHtml";
import { decodeHtmlEntities, formatTextWithColon, parseJsonOrUndefined } from "@/lib/utils";
import { SupportedLocale } from "@/i18n";
import { Multipleprint, Multiplesample } from "@/lib/types";
import {
  DimensionsFormatting,
  hasDisplayableDimensions,
} from "@/lib/compute-field/shared/dimensions";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { CollectorNameForOverviewCF } from "@/lib/compute-field/collector";
import { DimensionsDTO } from "@/lib/shared-types";
import { useMemo } from "react";
import { MultipleprintPortfolioBlock } from "./MultipleprintPortfolioBlock";

interface MultipleprintOverviewProps {
  data: Multipleprint;
  dimensionsSummaryCalculated: string;
  supportTechniqueCalculated: string;
  signatureCalculated: string;
  inscriptionDosCalculated: string;
  locale: SupportedLocale;
  t: (key: string) => string;
  itemType?: "MULTIPLE" | "EXEMPLAIRE";
  isExemplaire?: boolean;
  selectedSample?: Multiplesample | null;
  /** Monté dans le canvas du CMS avancé. */
  embedded?: boolean;
}

interface SelectedSampleDetails extends Multiplesample {
  exemplaireDansEstampesMultiplesDescriptionDateTirage?: string;
  exemplaireDansEstampesMultiplesDescriptionType?: string;
  exemplaireDansEstampesMultiplesDescriptionPrinter?: string;
  exemplaireDansEstampesMultiplesDescriptionEditor?: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli?: boolean;
  exemplaireDansEstampesDimensions?: string | DimensionsDTO;
  exemplaireDansEstampesDimensionsImage?: string | DimensionsDTO;
  exemplaireDansEstampesMultiplesTechnique?: Record<string, string>;
  exemplaireDansEstampesMultiplesSupport?: Record<string, string>;
  exemplaireDansEstampesMultiplesEditionNumber?: Record<string, string>;
  exemplaireDansEstampesMultiplesSignatureInscription?: Record<string, string>;
  collector?: {
    collectionneursDescriptionNom?: string;
    collectionneursDescriptionPrenom?: string;
    collectionneursDescriptionCollectionPublique?: boolean;
    collectionneursDescriptionVille?: string;
    collectionneursDescriptionVillePublished?: boolean;
    collectionneursDescriptionPays?: string;
    collectionneursDescriptionPaysPublished?: boolean;
  };
}

function getMultilingualField(
  value: Record<string, string> | undefined,
  locale: string,
) {
  if (!value || typeof value !== "object") return "";
  return value[locale] || Object.values(value)[0] || "";
}

export function MultipleprintOverview({
  data,
  dimensionsSummaryCalculated,
  supportTechniqueCalculated,
  signatureCalculated,
  inscriptionDosCalculated,
  locale,
  t,
  itemType,
  isExemplaire,
  selectedSample,
  embedded,
}: MultipleprintOverviewProps) {
  const sample = selectedSample as SelectedSampleDetails | null | undefined;
  const isSampleMode = itemType === "EXEMPLAIRE" || !!isExemplaire;
  const dimensionsWithMargins = useMemo(() => {
    const raw = sample?.exemplaireDansEstampesDimensions;
    if (!raw) return null;
    return typeof raw === "string" ? parseJsonOrUndefined<DimensionsDTO>(raw) : raw;
  }, [sample?.exemplaireDansEstampesDimensions]);
  const dimensionsWithoutMargins = useMemo(() => {
    const raw = sample?.exemplaireDansEstampesDimensionsImage;
    if (!raw) return null;
    return typeof raw === "string" ? parseJsonOrUndefined<DimensionsDTO>(raw) : raw;
  }, [sample?.exemplaireDansEstampesDimensionsImage]);

  // Des dimensions stockees mais vides (objet JSON sans aucune mesure) rendaient
  // quand meme le libelle suivi de rien (ticket #1055).
  const hasDimensionsWithMargins =
    hasDisplayableDimensions(dimensionsWithMargins);
  const hasDimensionsWithoutMargins = hasDisplayableDimensions(
    dimensionsWithoutMargins,
  );
  const editionNumberValue = getMultilingualField(
    sample?.exemplaireDansEstampesMultiplesEditionNumber,
    locale,
  );
  const signatureInscriptionValue = getMultilingualField(
    sample?.exemplaireDansEstampesMultiplesSignatureInscription,
    locale,
  );

  return (
    <div className="mb-1">
      {isSampleMode && sample?.exemplaireDansEstampesMultiplesDescriptionDateTirage ? (
        <p className="font-normal text-base">
          {t("singleMultipleprintExemplaireDateTirage")}{" "}
          {sample.exemplaireDansEstampesMultiplesDescriptionDateTirage}
        </p>
      ) : data?.estampesMultiplesDescriptionDateDeCreation ? (
        <p className="font-normal text-base">
          {data.estampesMultiplesDescriptionDateDeCreation}
        </p>
      ) : null}
      <div className="my-1 subpixel-antialiased">
        {data?.estampesMultiplesDescriptionSousTitre && (
          <p className="font-normal text-sm">
            {t("estampesMultiplesDescriptionSousTitre")} : {data.estampesMultiplesDescriptionSousTitre}
          </p>
        )}
        {data?.estampesMultiplesDescriptionAutreTitre && (
          <p className="font-normal text-sm">
            {t("estampesMultiplesDescriptionAutreTitre")} : {data.estampesMultiplesDescriptionAutreTitre}
          </p>
        )}
      </div>
      {isSampleMode && sample?.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire &&
        sample.exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli !== false && (
          <p className="font-normal text-sm">
            {t("singleMultipleprintExemplaireNumeroInventaire")}{" "}
            {sample.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire}
          </p>
      )}
      {isSampleMode && sample?.exemplaireDansEstampesMultiplesDescriptionType && (
        <p className="font-normal text-sm">
          {t("singleMultipleprintExemplaireType")}{" "}
          {sample.exemplaireDansEstampesMultiplesDescriptionType}
        </p>
      )}
      {isSampleMode && sample?.exemplaireDansEstampesMultiplesDescriptionPrinter && (
        <p className="font-normal text-sm">
          {t("singleMultipleprintFullscreenTirageRealisePar")}{" "}
          {sample.exemplaireDansEstampesMultiplesDescriptionPrinter}
        </p>
      )}
      {isSampleMode && sample?.exemplaireDansEstampesMultiplesDescriptionEditor && (
        <p className="font-normal text-sm">
          {t("exemplaireDansEstampesMultiplesDescriptionEditor")}{" "}
          {sample.exemplaireDansEstampesMultiplesDescriptionEditor}
        </p>
      )}
      {isSampleMode &&
      (sample?.exemplaireDansEstampesMultiplesTechnique ||
        sample?.exemplaireDansEstampesMultiplesSupport) ? (
        <p className="font-normal text-sm">
          {t("singleMultipleprintExemplaireTechniqueSupport")}{" "}
          <SupportTechniqueCF
            input={{
              technique: sample.exemplaireDansEstampesMultiplesTechnique,
              support: sample.exemplaireDansEstampesMultiplesSupport,
              preposition: t("computeField.techniqueSupportPreposition"),
            }}
            language={locale}
            lineBreak={false}
          />
        </p>
      ) : (
        supportTechniqueCalculated && (
          <div
            className="font-normal text-sm"
            dangerouslySetInnerHTML={{ __html: supportTechniqueCalculated }}
          />
        )
      )}
      {isSampleMode && hasDimensionsWithMargins && dimensionsWithMargins ? (
        <div className="flex items-center gap-2">
          <p className="font-normal text-sm">
            {formatTextWithColon(
              t(
                hasDimensionsWithoutMargins
                  ? "singleMultipleprintExemplaireDimensionsSheet"
                  : "singleMultipleprintExemplaireDimensions",
              ),
              locale,
              "",
            )}
          </p>
          <div className="font-normal text-sm">
            <DimensionsFormatting
              input={dimensionsWithMargins}
              language={locale}
              lineBreak={false}
            />
          </div>
        </div>
      ) : (
        dimensionsSummaryCalculated && (
          <div
            className="font-normal text-sm"
            dangerouslySetInnerHTML={{ __html: dimensionsSummaryCalculated }}
          />
        )
      )}
      {isSampleMode && hasDimensionsWithoutMargins && dimensionsWithoutMargins ? (
        <div className="flex items-center gap-2">
          <p className="font-normal text-sm">
            {formatTextWithColon(
              t("singleMultipleprintExemplaireDimensionsImage"),
              locale,
              "",
            )}
          </p>
          <div className="font-normal text-sm">
            <DimensionsFormatting
              input={dimensionsWithoutMargins}
              language={locale}
              lineBreak={false}
            />
          </div>
        </div>
      ) : null}
      {!isSampleMode &&
      ((data?.estampesMultiplesDescriptionNumeroInventairePublished === true &&
        data?.estampesMultiplesDescriptionNumeroInventaire?.trim()) ||
        (data?.estampesMultiplesDescriptionNumeroAncienPublished === true &&
          data?.estampesMultiplesDescriptionNumeroAncien?.trim()) ||
        (data?.estampesMultiplesDescriptionNumeroAutresPublished === true &&
          data?.estampesMultiplesDescriptionNumeroAutres?.trim())) ? (
        <div className="mt-2">
          {data?.estampesMultiplesDescriptionNumeroInventairePublished === true &&
            data?.estampesMultiplesDescriptionNumeroInventaire?.trim() && (
              <p className="font-normal text-sm">
                {formatTextWithColon(
                  t("estampesMultiplesDescriptionNumeroInventaire"),
                  locale,
                  data.estampesMultiplesDescriptionNumeroInventaire.trim(),
                )}
              </p>
            )}
          {data?.estampesMultiplesDescriptionNumeroAncienPublished === true &&
            data?.estampesMultiplesDescriptionNumeroAncien?.trim() && (
              <p className="font-normal text-sm">
                {formatTextWithColon(
                  t("estampesMultiplesDescriptionNumeroAncien"),
                  locale,
                  data.estampesMultiplesDescriptionNumeroAncien.trim(),
                )}
              </p>
            )}
          {data?.estampesMultiplesDescriptionNumeroAutresPublished === true &&
            data?.estampesMultiplesDescriptionNumeroAutres?.trim() && (
              <p className="font-normal text-sm">
                {formatTextWithColon(
                  t("estampesMultiplesDescriptionNumeroAutres"),
                  locale,
                  data.estampesMultiplesDescriptionNumeroAutres.trim(),
                )}
              </p>
            )}
        </div>
      ) : null}
      {isSampleMode && editionNumberValue && (
        <p className="font-normal text-sm">
          {t("singleMultipleprintExemplaireEditionNumber")} {editionNumberValue}
        </p>
      )}
      {isSampleMode && signatureInscriptionValue ? (
        <p className="font-normal text-sm">
          {t("singleMultipleprintFullscreenSignature")}{" "}
          {decodeHtmlEntities(signatureInscriptionValue)}
        </p>
      ) : (
        signatureCalculated && (
          <div
            className="font-normal text-sm"
            dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(signatureCalculated) }}
          />
        )
      )}
      {isSampleMode && sample?.collector && (
        <p className="font-normal text-sm">
          {t("singleMultipleprintExemplaireCollector")}{" "}
          <CollectorNameForOverviewCF
            input={{
              collectionneursDescriptionNom: sample.collector.collectionneursDescriptionNom ?? "",
              collectionneursDescriptionPrenom: sample.collector.collectionneursDescriptionPrenom ?? "",
              collectionneursDescriptionCollectionPublique:
                sample.collector.collectionneursDescriptionCollectionPublique ?? false,
              collectionneursDescriptionVille: sample.collector.collectionneursDescriptionVille,
              collectionneursDescriptionVillePublished:
                sample.collector.collectionneursDescriptionVillePublished,
              collectionneursDescriptionPays: sample.collector.collectionneursDescriptionPays,
              collectionneursDescriptionPaysPublished:
                sample.collector.collectionneursDescriptionPaysPublished,
              showCity: true,
            }}
            language={locale}
            translation={t}
          />
        </p>
      )}
      {!isSampleMode &&
        inscriptionDosCalculated &&
        data?.estampesMultiplesDescriptionInscriptionsDosPublished?.[locale] && (
          <div
            className="font-normal text-sm"
            dangerouslySetInnerHTML={{ __html: inscriptionDosCalculated }}
          />
      )}
      <MultipleprintPortfolioBlock
        data={data}
        locale={locale}
        t={t}
        embedded={embedded}
        partOfOnly={isSampleMode}
      />
      {data?.estampesMultiplesDescriptionNotesPubliquesMultilingual?.[locale] && (
        <div className="my-1 mt-4 flex flex-col gap-4 font-medium text-sm">
          {data?.estampesMultiplesDescriptionNotesPubliquesLabelPublished !==
            false && t("singleMultipleprintSubtitlePublicNote")}
          <ReadMoreHtml
            className="font-normal text-sm"
            locale={locale}
            html={
              data.estampesMultiplesDescriptionNotesPubliquesMultilingual[locale]
            }
          />
        </div>
      )}
      {isSampleMode &&
        sample?.exemplaireDansEstampesMultiplesRemarquesPubliquesPublished ===
          true &&
        sample?.exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual?.[
          locale
        ] && (
          <div className="my-1 mt-4 flex flex-col gap-4 font-medium text-sm">
            {sample?.exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli !==
              false && t("singleArtworkSubtitlePublicNote")}
            <ReadMoreHtml
              className="font-normal text-sm"
              locale={locale}
              html={
                sample
                  .exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual[
                  locale
                ]
              }
            />
          </div>
        )}
    </div>
  );
}
